// Cambridge-style answer-key DSL (PRD §7 D24, PHASE.md Phase 9).
//
// matchAnswer(userInput, keyPattern) tells the grader whether a student's
// answer is acceptable. The matcher itself is a pure function — no I/O, no
// randomness — so the Phase 20 submit-attempt Edge Function and the client
// preview UI can share it. This file is mirrored byte-for-byte to
// supabase/functions/_shared/answer-key-dsl.ts (see CLAUDE.md "mirror"
// pattern); change one, change the other.
//
// PATTERN GRAMMAR
//   pattern := alternation
//   alternation := sequence ('|' sequence)*
//   sequence := atom*
//   atom := text | '(' sequence ')'        // optional group
//
//   Examples:
//     '(the) sun'    matches 'sun' and 'the sun'
//     'detail(s)'    matches 'detail' and 'details'
//     'red|blue'     matches 'red' or 'blue'
//     '(the )colour(s)' matches 'colour', 'colours', 'the colour', 'the colours'
//
// NORMALIZATION (applied to both user input AND every expanded key variant
// before comparison)
//   - lowercase + trim
//   - smart quotes → ASCII (‘’ → ', “” → ")
//   - English contractions expanded (don't → do not, etc.)
//   - hyphens / en-dash / em-dash → space
//   - US ↔ UK spelling: normalized to UK form
//   - integers 0-100 → English words ('12' → 'twelve', 'twenty-one' → 'twenty one')
//   - trailing punctuation stripped, whitespace collapsed
//
// SAFETY
//   - expandOptionals is bounded at 64 variants (2^6 optional groups). Past
//     that we return the variants we have. Real IELTS keys carry 0-2 groups.
//   - all regexes are bounded character classes with no nested repetition,
//     so they're linear-time (no catastrophic backtracking).

export interface MatchResult {
  ok: boolean;
  /** The normalized form of the user input — useful for debug logs. */
  normalized: string;
  /** Why a non-match failed, when the reason is more specific than
   *  "the text differs" (currently only the official word-limit rule). */
  failure?: 'word_limit';
}

export type MatchMode = 'strict' | 'lenient';

export interface MatchOptions {
  /** 'lenient' tolerates a missing/extra leading article ("the 1960s" ≡
   *  "1960s") — the official rule for Short-Answer Questions ONLY; every
   *  other type stays 'strict' (the default). */
  mode?: MatchMode;
  /** Official word-limit rule: an answer over the printed limit is wrong even
   *  when its content is right. Counted with the official tokenizer below
   *  (hyphenated compound = 1 word, "four hundred" = 1 number token). */
  wordLimit?: number | null;
}

const MAX_EXPANDED_VARIANTS = 64;

const ONES: readonly string[] = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
];

const TENS: readonly string[] = [
  '',
  '',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
];

// Contractions are matched on the *post-smart-quote* (ASCII) form. The
// expansion happens before apostrophes are stripped.
const CONTRACTIONS: ReadonlyArray<readonly [string, string]> = [
  ["don't", 'do not'],
  ["doesn't", 'does not'],
  ["didn't", 'did not'],
  ["won't", 'will not'],
  ["wouldn't", 'would not'],
  ["can't", 'cannot'],
  ["couldn't", 'could not'],
  ["shouldn't", 'should not'],
  ["wasn't", 'was not'],
  ["weren't", 'were not'],
  ["isn't", 'is not'],
  ["aren't", 'are not'],
  ["haven't", 'have not'],
  ["hasn't", 'has not'],
  ["hadn't", 'had not'],
  ["it's", 'it is'],
  ["that's", 'that is'],
  ["what's", 'what is'],
  ["he's", 'he is'],
  ["she's", 'she is'],
  ["i'm", 'i am'],
  ["you're", 'you are'],
  ["we're", 'we are'],
  ["they're", 'they are'],
  ["i've", 'i have'],
  ["you've", 'you have'],
  ["we've", 'we have'],
  ["they've", 'they have'],
  ["i'll", 'i will'],
  ["you'll", 'you will'],
  ["we'll", 'we will'],
  ["they'll", 'they will'],
];

// US → UK normalization (canonical = UK). Each entry is [pattern, replacement].
// Patterns are bounded; the (s|d|r)? group captures common inflections so we
// don't need a separate entry per form. Order matters only for overlapping
// rules — none here overlap, so they're independent.
const US_UK_PAIRS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bcolor(s|ed|ing|ful|less)?\b/gi, 'colour$1'],
  [/\bflavor(s|ed|ing|ful|less)?\b/gi, 'flavour$1'],
  [/\bhonor(s|ed|ing|able)?\b/gi, 'honour$1'],
  [/\blabor(s|ed|ing)?\b/gi, 'labour$1'],
  [/\bneighbor(s|hood|ly)?\b/gi, 'neighbour$1'],
  [/\bbehavior(s|al)?\b/gi, 'behaviour$1'],
  [/\bharbor(s|ed|ing)?\b/gi, 'harbour$1'],
  [/\bcenter(s|ed|ing)?\b/gi, 'centre$1'],
  [/\btheater(s)?\b/gi, 'theatre$1'],
  [/\bmeter(s)?\b/gi, 'metre$1'],
  [/\borganize(s|d|r|rs)?\b/gi, 'organise$1'],
  [/\brealize(s|d)?\b/gi, 'realise$1'],
  [/\brecognize(s|d)?\b/gi, 'recognise$1'],
  [/\banalyze(s|d|r|rs)?\b/gi, 'analyse$1'],
  [/\bcatalog(s|ed|ing|ue)?\b/gi, 'catalogue'],
  [/\bprogram(s)?\b/gi, 'programme$1'],
  [/\bdefense(s)?\b/gi, 'defence$1'],
  [/\boffense(s)?\b/gi, 'offence$1'],
  [/\blicense(s)?\b/gi, 'licence$1'],
  [/\btraveling\b/gi, 'travelling'],
  [/\btraveled\b/gi, 'travelled'],
  [/\bjudgment(s)?\b/gi, 'judgement$1'],
];

export function matchAnswer(
  userInput: string,
  keyPattern: string,
  options?: MatchOptions,
): MatchResult {
  const normalized = normalize(userInput);
  if (normalized === '') return { ok: false, normalized: '' };

  // Official rule: exceeding the printed word limit is WRONG regardless of
  // content. Checked on the raw input (the tokenizer has its own rules for
  // hyphens and number phrases — normalize() would distort them).
  const limit = options?.wordLimit;
  if (typeof limit === 'number' && limit > 0 && countAnswerWords(userInput) > limit) {
    return { ok: false, normalized, failure: 'word_limit' };
  }

  const lenient = options?.mode === 'lenient';
  const target = lenient ? stripLeadingArticle(normalized) : normalized;
  const variants = expandKey(keyPattern);
  for (const v of variants) {
    const candidate = lenient ? stripLeadingArticle(normalize(v)) : normalize(v);
    if (candidate === target) {
      return { ok: true, normalized };
    }
  }
  return { ok: false, normalized };
}

/** Leading-article tolerance for lenient mode (Short-Answer Questions): with
 *  no carrier sentence, "the 1960s" and "1960s" are the same answer. Applied
 *  to BOTH sides so key authors need not encode "(the )" by hand. */
function stripLeadingArticle(s: string): string {
  return s.replace(/^(?:the|an|a)\s+/, '');
}

// ---------------------------------------------------------------------------
// Official word counting (the shared tokenizer for word-limit rules)
// ---------------------------------------------------------------------------
//
// Rules (see Reading spec "shared validators"):
//   • hyphenated compounds are ONE word ("mother-in-law", "check-in")
//   • a spelled-out number phrase is ONE token ("four hundred" = 1;
//     "four hundred years" = 2: number + word)
//   • numeric figures are one token regardless of digits ("400", "1,000")
// ielts-taxonomy.ts keeps a byte-equivalent private copy (that file must stay
// import-free for its byte-identical Deno mirror) — change both together;
// answer-key-dsl.test.ts holds the parity test that keeps them honest.

const NUMBER_WORDS: ReadonlySet<string> = new Set([
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
  'hundred',
  'thousand',
  'million',
  'billion',
]);

function numberWordOf(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isNumberWord(raw: string): boolean {
  return NUMBER_WORDS.has(numberWordOf(raw));
}

function isNumberToken(raw: string): boolean {
  const cleaned = numberWordOf(raw);
  return NUMBER_WORDS.has(cleaned) || /^\d+$/.test(cleaned);
}

/** Count an answer's words by the official IELTS convention. */
export function countAnswerWords(answer: string): number {
  const rawWords = answer.trim().split(/\s+/).filter(Boolean);
  let count = 0;
  let i = 0;
  while (i < rawWords.length) {
    const word = rawWords.at(i) ?? '';
    if (isNumberToken(word)) {
      // Consume this and any immediately following spelled number-words as
      // ONE token ("four hundred" → 1; "four hundred years" → 1 + "years").
      let j = i + 1;
      while (j < rawWords.length && isNumberWord(rawWords.at(j) ?? '')) j += 1;
      i = j;
    } else {
      i += 1;
    }
    count += 1;
  }
  return count;
}

/** Expand a key pattern into its acceptable literal variants, for the builder's
 * live "Acceptable answers" preview. Bounded at 64 (see expandKey). */
export function expandAnswerKey(keyPattern: string): string[] {
  return expandKey(keyPattern);
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

function normalize(raw: string): string {
  let s = raw.toLowerCase().trim();

  // 1. Smart quotes → ASCII so the contraction table is plain.
  s = s.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');

  // 2. Contractions ("don't" → "do not") before we strip apostrophes.
  s = expandContractions(s);

  // 3. Strip remaining apostrophes + double-quotes.
  s = s.replace(/['"]/g, '');

  // 4. Hyphens (ASCII -, en-dash, em-dash) → space.
  s = s.replace(/[-–—]/g, ' ');

  // 5. Strip non-essential punctuation.
  s = s.replace(/[.,!?;:()/\\]/g, '');

  // 6. US → UK normalization.
  s = normalizeUsUk(s);

  // 7. Digits → words (0..100).
  s = digitsToWords(s);

  // 8. Collapse whitespace.
  s = s.replace(/\s+/g, ' ').trim();

  return s;
}

function expandContractions(s: string): string {
  let out = s;
  for (const [from, to] of CONTRACTIONS) {
    // \b…\b ensures we don't match inside larger tokens.
    // eslint-disable-next-line security/detect-non-literal-regexp -- `from` is a literal string in the const CONTRACTIONS table, escapeForRegex covers the metachar set
    const re = new RegExp(`\\b${escapeForRegex(from)}\\b`, 'gi');
    out = out.replace(re, to);
  }
  return out;
}

function normalizeUsUk(s: string): string {
  let out = s;
  for (const [pattern, replacement] of US_UK_PAIRS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

function digitsToWords(s: string): string {
  return s.replace(/\b\d+\b/g, (match) => {
    const n = Number.parseInt(match, 10);
    if (Number.isNaN(n) || n < 0 || n > 100) return match;
    return numberToWords(n);
  });
}

function numberToWords(n: number): string {
  // All indices below are bounded by integer arithmetic on `n` (checked
  // 0..100 at the call site); no user-controlled key reaches these arrays.
  /* eslint-disable security/detect-object-injection */
  if (n <= 20) return ONES[n] ?? String(n);
  if (n === 100) return 'one hundred';
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  const tensWord = TENS[tens] ?? '';
  if (ones === 0) return tensWord;
  const onesWord = ONES[ones] ?? '';
  return `${tensWord} ${onesWord}`;
  /* eslint-enable security/detect-object-injection */
}

function escapeForRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ---------------------------------------------------------------------------
// Pattern expansion
// ---------------------------------------------------------------------------

function expandKey(key: string): string[] {
  const alternations = splitTopLevel(key, '|');
  const out: string[] = [];
  for (const branch of alternations) {
    for (const variant of expandOptionals(branch)) {
      if (out.length >= MAX_EXPANDED_VARIANTS) return out;
      out.push(variant);
    }
  }
  return out;
}

function splitTopLevel(input: string, sep: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let buf = '';
  for (const ch of input) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === sep && depth === 0) {
      parts.push(buf);
      buf = '';
    } else {
      buf += ch;
    }
  }
  parts.push(buf);
  return parts;
}

function expandOptionals(input: string): string[] {
  // Find the first balanced `( ... )` group; recurse on the two variants
  // (group present / group absent).
  let depth = 0;
  let groupStart = -1;
  for (let i = 0; i < input.length; i++) {
    // eslint-disable-next-line security/detect-object-injection -- `i` is the integer loop counter; not user input
    const ch = input[i];
    if (ch === '(') {
      if (depth === 0) groupStart = i;
      depth++;
    } else if (ch === ')') {
      depth--;
      if (depth === 0 && groupStart >= 0) {
        const before = input.slice(0, groupStart);
        const inside = input.slice(groupStart + 1, i);
        const after = input.slice(i + 1);
        const withGroup = expandOptionals(before + inside + after);
        const withoutGroup = expandOptionals(before + after);
        const merged = [...withGroup];
        for (const v of withoutGroup) if (!merged.includes(v)) merged.push(v);
        return merged.slice(0, MAX_EXPANDED_VARIANTS);
      }
    }
  }
  // No more groups — return the literal.
  return [input];
}
